import { useEffect, useRef, useState } from 'react';
import { TelegramWidgetUser } from '@/api/auth';
import { Button } from '@/components/ui/button';
import { Send } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';

interface Props {
  onAuth: (user: TelegramWidgetUser) => void;
  /** When false, the OIDC button still renders but is disabled and
   *  shows a "TG blocked — try VPN" subline. Lets users see the
   *  option exists even when their network can't reach it. Null
   *  means the probe is still in flight; render nothing. */
  reachable?: boolean | null;
  /** Override the default "Войти через Telegram" label — used on
   *  the settings page where the action is "Привязать". */
  label?: string;
  /** Override where the OIDC callback should land the user after
   *  the redirect round-trip. Defaults to the `?next=` query param
   *  on the current URL, falling back to `/`. The settings page
   *  passes `/settings/account` so binding finishes there. */
  nextPath?: string;
  /** When true, render the OIDC button compact (auto-width, smaller
   *  height) — used inside cards on the settings page. */
  compact?: boolean;
}

declare global {
  interface Window {
    __tgWidgetCallback?: (user: TelegramWidgetUser) => void;
  }
}

const BOT_NAME = import.meta.env.VITE_TELEGRAM_BOT_NAME as string | undefined;
const OIDC_CLIENT_ID = import.meta.env.VITE_TELEGRAM_OIDC_CLIENT_ID as string | undefined;

// TG's OIDC endpoint — hardcoded since it doesn't change. If TG ever
// rotates it, the discovery doc is at
// https://oauth.telegram.org/.well-known/openid-configuration
const TG_OIDC_AUTH_URL = 'https://oauth.telegram.org/auth';

// Where TG redirects the browser back to with ?code=&state=. Kept in
// sync with the value sent at /token exchange time (TG checks it).
function oidcRedirectUri(): string {
  return `${window.location.origin}/auth/tg/callback`;
}

/** True when *some* TG sign-in path is configured for this build —
 *  either the legacy widget (VITE_TELEGRAM_BOT_NAME) or the new OIDC
 *  flow (VITE_TELEGRAM_OIDC_CLIENT_ID). Pages should hide TG prompts
 *  altogether when this is false so users don't see dead instructions. */
export const isTelegramConfigured = !!BOT_NAME || !!OIDC_CLIENT_ID;

/** True when the OIDC path is the one this build uses. The OIDC
 *  button is a static element under our own styling control; the
 *  legacy widget injects a TG-hosted iframe whose reachability has
 *  to be probed at runtime. LoginPage uses this to skip the iframe
 *  probe + render the divider unconditionally in OIDC mode. */
export const isTelegramOidcMode = !!OIDC_CLIENT_ID;

// ── OIDC helpers ───────────────────────────────────────────────────

function randomUrlSafe(byteLen: number): string {
  const bytes = new Uint8Array(byteLen);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function sha256UrlSafe(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  const bytes = new Uint8Array(buf);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export const TG_OIDC_STATE_KEY = 'tg_oidc_state';
export const TG_OIDC_VERIFIER_KEY = 'tg_oidc_verifier';
export const TG_OIDC_NEXT_KEY = 'tg_oidc_next';

async function startOidcFlow(nextPath: string) {
  const state = randomUrlSafe(16);
  const verifier = randomUrlSafe(32);
  const challenge = await sha256UrlSafe(verifier);
  sessionStorage.setItem(TG_OIDC_STATE_KEY, state);
  sessionStorage.setItem(TG_OIDC_VERIFIER_KEY, verifier);
  sessionStorage.setItem(TG_OIDC_NEXT_KEY, nextPath);

  const params = new URLSearchParams({
    client_id: OIDC_CLIENT_ID!,
    redirect_uri: oidcRedirectUri(),
    response_type: 'code',
    scope: 'openid profile',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  window.location.href = `${TG_OIDC_AUTH_URL}?${params.toString()}`;
}

// ── Component ──────────────────────────────────────────────────────

export function TelegramLoginButton({
  onAuth,
  reachable = true,
  label,
  nextPath,
  compact = false,
}: Props) {
  const { t } = useT();
  const containerRef = useRef<HTMLDivElement>(null);
  // Set when the user taps the (gray) button before the reachability probe has
  // resolved. A reachable TG turns the button blue in <1s, so a tap on a still-gray
  // button means the probe is slow/failing — reveal the "unavailable" verdict now
  // instead of making them wait out the full ~30s budget. Self-corrects: if the
  // background probe later succeeds, the button turns blue and the hint clears.
  const [impatientReveal, setImpatientReveal] = useState(false);

  // Legacy widget — only mounts when OIDC isn't configured AND the bot
  // name env is present. The effect guards itself so hook order stays
  // stable across renders regardless of which branch we take below.
  useEffect(() => {
    if (OIDC_CLIENT_ID || !BOT_NAME || !containerRef.current) return;

    window.__tgWidgetCallback = onAuth;

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', BOT_NAME);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', '__tgWidgetCallback(user)');
    script.setAttribute('data-request-access', 'write');
    script.async = true;

    const container = containerRef.current;
    container.appendChild(script);

    return () => {
      delete window.__tgWidgetCallback;
      if (script.parentNode === container) {
        container.removeChild(script);
      }
    };
  }, [onAuth]);

  // OIDC path — render a single button that kicks off the redirect.
  // No iframe, no widget JS, no third-party script tag. The button
  // is always present; its state cycles through probing → ready →
  // (maybe) disabled. Reserving the slot from the first render
  // avoids the layout shift / blink we used to have when the TG
  // block popped in after the probe finished.
  if (OIDC_CLIENT_ID) {
    const probing = reachable === null;
    const blocked = reachable === false;
    const ready = reachable === true;
    // Show the "unavailable" verdict when TG is confirmed blocked, OR the user got
    // impatient and tapped the still-gray button mid-probe.
    const showBlockedHint = blocked || (probing && impatientReveal);
    const handleClick = () => {
      // Confirmed blocked → login is gated (Alexey 2026-07-10: TG unreachable ⇒ login
      // unavailable; don't send users into an OIDC redirect that will just hang).
      if (blocked) return;
      // Still probing → don't redirect on an unresolved probe; just surface the status
      // early (a reachable TG would already have turned the button blue).
      if (probing) {
        setImpatientReveal(true);
        return;
      }
      // ready — preserve the destination across the round-trip. Default: the `?next=`
      // query param. Override: `nextPath` (settings page passes /settings/account).
      const next =
        nextPath
        || new URLSearchParams(window.location.search).get('next')
        || '/';
      void startOidcFlow(next);
    };
    const displayLabel = label ?? t('auth.signInWithTelegram');
    const sizeClasses = compact
      ? 'h-9 w-fit px-4'
      : 'h-11 w-full';
    const wrapperClasses = compact
      ? 'flex w-fit flex-col gap-1.5'
      : 'flex w-full flex-col gap-1.5';

    // No spinner/shimmer during the probe (Alexey 2026-07-10): an element that loads
    // forever on a blocked network reads as broken. The button renders GRAY and inert
    // while probing, turns Telegram-blue only once the host is confirmed reachable,
    // and shows the unavailable hint if the probe fails (or on an impatient tap).
    // `disabled` only when confirmed blocked — during probing it stays clickable so
    // the impatient tap registers.
    return (
      <div className={wrapperClasses}>
        <Button
          type="button"
          onClick={handleClick}
          disabled={blocked}
          aria-busy={probing}
          title={showBlockedHint ? t('auth.telegramBlocked') : undefined}
          className={`gap-2 rounded-[10px] border-0 font-medium ${sizeClasses} ${
            ready
              ? 'bg-[#229ED9] text-white hover:bg-[#1d8bc0]'
              : 'bg-muted text-muted-foreground hover:bg-muted disabled:bg-muted disabled:text-muted-foreground'
          }`}
        >
          <Send className="size-4" />
          {displayLabel}
        </Button>
        {showBlockedHint && (
          <p className="text-center text-[11px] text-muted-foreground">
            {t('auth.telegramBlocked')}
          </p>
        )}
      </div>
    );
  }

  if (!BOT_NAME) return null;

  return <div ref={containerRef} className="flex justify-center" />;
}
